import { apiTypes } from '@cmg/common';
import { DataGridContextProvider, getDataSource, ServerDataGrid } from '@cmg/data-grid';
import React, { useMemo } from 'react';

import systemManagementApiClient from '../../../../../common/api/systemManagementApiClient';
import type { Account } from '../../../../../types/domain/account/account';
import columns from './TraitAccountListGridColumns';

export type Props = {
  traitCode: string;
};

/**
 * Displays CMG Accounts with limited info relevant to the Trait Detail Page.
 *
 * Uses a REST adapter inside getDataSource: translates ag-grid's { skip, take, sortModel }
 * into the /admin/accounts REST params. See ADR 0003.
 */
const TraitAccountList: React.FC<Props> = ({ traitCode }) => {
  const datasource = useMemo(
    () =>
      getDataSource<Account, Record<string, never>>({
        getData: async (variables, request) => {
          const { skip = 0, take = 100 } = variables;
          const page = Math.floor(skip / take) + 1;
          const sortModel = request.sortModel[0];

          const response = await systemManagementApiClient.admin.getAccounts({
            page,
            perPage: take,
            traits: [traitCode],
            ...(sortModel && {
              orderField: sortModel.colId,
              orderDirection:
                sortModel.sort === 'asc' ? apiTypes.SortDirection.ASC : apiTypes.SortDirection.DESC,
            }),
          });

          if (!response.ok) {
            throw new Error(response.data?.error?.message ?? 'Failed to load accounts');
          }

          return {
            rowData: response.data.data,
            rowCount: response.data.pagination.totalCount,
          };
        },
      }),
    [traitCode]
  );

  return (
    <div style={{ flex: 1, minHeight: 0 }}>
      <DataGridContextProvider>
        <ServerDataGrid<Account>
          serverSideDatasource={datasource}
          columnDefs={columns}
          sortLimit={1}
        />
      </DataGridContextProvider>
    </div>
  );
};

export default TraitAccountList;
